import { createTheme, type ThemeOptions } from "@mui/material/styles";
import { colors, darkColors } from "../theme/colors";
import { type ColorPalette } from "../theme/presets";
import { typography, getTypography, type ThemeDensity } from "../theme/typography";
import { spacing } from "../theme/spacing";
import { breakpoints } from "../theme/breakpoints";

export const baseThemeOptions: ThemeOptions = {
  palette: colors,
  typography,
  spacing,
  breakpoints,
  shape: {
    borderRadius: 4,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
        },
        sizeSmall: ({ theme }) => ({
          fontSize: theme.typography.body2.fontSize,
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundImage: "none",
          backgroundColor: theme.palette.background.paper,
        }),
      },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          borderTop: "1px solid rgba(0, 0, 0, 0.12)",
        },
      },
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          minWidth: 80,
          maxWidth: 168,
          "&.Mui-selected": {
            fontSize: "0.75rem",
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          textTransform: 'none',
          fontWeight: 500,
          fontSize: theme.typography.body2.fontSize,
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 8,
          ...(theme.palette.mode === 'light' && {
            border: `1px solid ${theme.palette.divider}`,
          }),
        }),
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...(theme.palette.mode === 'light' && {
            '&:not(.Mui-active):not(.Mui-completed)': {
              color: theme.palette.grey[500],
            },
          }),
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        // In dark mode MUI uses alpha(color.main, 0.2) for filled chips, which
        // produces a near-invisible tint on dark surfaces. Override to use the
        // full palette color so chips are legible in both light and dark mode.
        colorWarning: ({ theme }) =>
          theme.palette.mode === 'dark'
            ? {
                backgroundColor: theme.palette.warning.main,
                color: theme.palette.warning.contrastText,
              }
            : {},
        colorError: ({ theme }) =>
          theme.palette.mode === 'dark'
            ? {
                backgroundColor: theme.palette.error.main,
                color: theme.palette.error.contrastText,
              }
            : {},
        colorSuccess: ({ theme }) =>
          theme.palette.mode === 'dark'
            ? {
                backgroundColor: theme.palette.success.main,
                color: theme.palette.success.contrastText,
              }
            : {},
        colorInfo: ({ theme }) =>
          theme.palette.mode === 'dark'
            ? {
                backgroundColor: theme.palette.info.main,
                color: theme.palette.info.contrastText,
              }
            : {},
        // secondary.main is a near-background slate in dark mode, so both the
        // default tint and the full main colour are invisible on dark cards.
        // Use the lighter secondary.dark shade instead so the chip reads.
        colorSecondary: ({ theme }) =>
          theme.palette.mode === 'dark'
            ? {
                backgroundColor: theme.palette.secondary.dark,
                color: theme.palette.secondary.contrastText,
              }
            : {},
      },
    },
  },
};

export const createBilldaTheme = (customOptions?: ThemeOptions) => {
  // Merge base options with custom options
  const mergedOptions = {
    ...baseThemeOptions,
    ...customOptions,
  };

  // Create the theme
  return createTheme(mergedOptions);
};

// Create a theme with a specific preset
export const createBilldaThemeWithPreset = (
  preset: ColorPalette,
  customOptions?: ThemeOptions
) => {
  const presetOptions: ThemeOptions = {
    palette: preset,
    typography,
    spacing,
    breakpoints,
    shape: {
      borderRadius: 4,
    },
    components: baseThemeOptions.components,
  };

  const mergedOptions = {
    ...presetOptions,
    ...customOptions,
  };

  return createTheme(mergedOptions);
};

// Create a theme with light/dark mode support
export const createBilldaThemeWithMode = (
  mode: 'light' | 'dark' = 'light',
  customOptions?: ThemeOptions,
  density: ThemeDensity = 'comfortable'
) => {
  const paletteColors = mode === 'light' ? colors : darkColors;

  const modeOptions: ThemeOptions = {
    palette: {
      mode,
      ...paletteColors,
    },
    typography: getTypography(density),
    spacing,
    breakpoints,
    shape: {
      borderRadius: 4,
    },
    components: baseThemeOptions.components,
  };

  const mergedOptions = {
    ...modeOptions,
    ...customOptions,
    components: {
      ...modeOptions.components,
      ...customOptions?.components,
    },
  };

  return createTheme(mergedOptions);
};
